import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, AlertTriangle } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: 'Real-Time Monitoring',
    description: 'Advanced AI-powered disaster detection and monitoring systems provide instant alerts and updates for your safety.',
    gradient: 'from-primary-400 to-primary-600',
    bgGradient: 'from-primary-400/10 to-primary-600/5',
  },
  {
    icon: Users,
    title: 'Community Network',
    description: 'Connect with neighbors, share resources, and coordinate relief efforts through our robust community platform.',
    gradient: 'from-accent-green to-primary-400',
    bgGradient: 'from-accent-green/10 to-primary-400/5',
  },
  {
    icon: AlertTriangle,
    title: 'Emergency Response',
    description: 'Instant access to emergency protocols, evacuation routes, and critical resources when you need them most.',
    gradient: 'from-accent-orange to-accent-red',
    bgGradient: 'from-accent-orange/10 to-accent-red/5',
  },
];

const FeatureCards = () => {
  return (
    <section className="py-20 px-4 relative">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent">
            Comprehensive Disaster Management
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Our platform combines cutting-edge technology with community-driven solutions 
            to provide the most effective disaster response and recovery tools.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className={`relative group cursor-pointer`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.bgGradient} rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-50 group-hover:opacity-75`} />
                
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-2xl p-8 h-full transition-all duration-300 group-hover:border-dark-600">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-primary-300 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-400/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureCards;